import type { ReactNode } from "react";
import { useState, useEffect, useRef, useMemo } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "../utils/cn";
import { Badge } from "../primitives/Badge";
import { Button } from "../primitives/Button";
import { Input } from "../primitives/Input";
import { Select } from "../primitives/Select";

function itemMatchesSearch(item: EntityListItem, query: string): boolean {
  if (!query.trim()) return true;
  const term = query.trim().toLowerCase();
  const title = (item.title ?? "").toLowerCase();
  const subtitle = (item.subtitle ?? "").toLowerCase();
  const info = (item.info ?? "").toLowerCase();
  return title.includes(term) || subtitle.includes(term) || info.includes(term);
}

type EntityListItem = {
  id: string;
  title: string;
  subtitle?: string;
  info?: string;
  icon: ReactNode;
  status?: { label: string; tone: "neutral" | "success" | "warning" | "danger" };
  groupHeader?: boolean;
  groupColor?: string;
};

type SectionItem = {
  id: string;
  label: string;
  icon: string;
  active?: boolean;
  onClick?: () => void;
};

type EntityListSidebarProps = {
  sections?: SectionItem[];
  sectionTitle?: string;
  items: EntityListItem[];
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  isExpanded: boolean;
  searchPlaceholder?: string;
  filterOptions?: string[];
  emptyMessage?: string;
  addActionLabel?: string;
  onAddAction?: () => void;
  defaultCollapsedGroups?: boolean;
};

export function EntityListSidebar({
  sections = [],
  sectionTitle = "Navigation",
  items,
  selectedId,
  onSelect,
  isExpanded,
  searchPlaceholder = "Rechercher...",
  filterOptions = ["Tous", "Actif", "Inactif"],
  emptyMessage = "Aucune entité disponible.",
  addActionLabel,
  onAddAction,
  defaultCollapsedGroups = false,
}: EntityListSidebarProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const hasInitializedCollapsed = useRef(false);

  useEffect(() => {
    if (!defaultCollapsedGroups || hasInitializedCollapsed.current || items.length === 0) return;
    const groupIds = items.filter((i) => i.groupHeader).map((i) => i.id);
    if (groupIds.length > 0) {
      setCollapsedGroups(new Set(groupIds));
      hasInitializedCollapsed.current = true;
    }
  }, [defaultCollapsedGroups, items]);

  const toggleGroup = (groupId: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // Filtrer les items selon la recherche (titre, sous-titre, info) ; garder un groupe si l'en-tête ou un enfant correspond
  const filteredItems = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    if (!term) return items;

    const result: EntityListItem[] = [];
    let i = 0;
    while (i < items.length) {
      const item = items[i];
      if (item.groupHeader) {
        const header = item;
        i++;
        const matchingChildren: EntityListItem[] = [];
        while (i < items.length && !items[i].groupHeader) {
          if (itemMatchesSearch(items[i], searchQuery)) {
            matchingChildren.push(items[i]);
          }
          i++;
        }
        const headerMatches = itemMatchesSearch(header, searchQuery);
        if (headerMatches || matchingChildren.length > 0) {
          result.push(header);
          result.push(...matchingChildren);
        }
      } else {
        if (itemMatchesSearch(item, searchQuery)) result.push(item);
        i++;
      }
    }
    return result;
  }, [items, searchQuery]);

  // Grouper les items filtrés (en-tête + enfants)
  const groupedItems: Array<{ header: EntityListItem; items: EntityListItem[] }> = useMemo(() => {
    const groups: Array<{ header: EntityListItem; items: EntityListItem[] }> = [];
    let currentGroup: { header: EntityListItem; items: EntityListItem[] } | null = null;

    filteredItems.forEach((item) => {
      if (item.groupHeader) {
        if (currentGroup) groups.push(currentGroup);
        currentGroup = { header: item, items: [] };
      } else {
        if (currentGroup) {
          currentGroup.items.push(item);
        } else {
          groups.push({ header: item, items: [] });
        }
      }
    });
    if (currentGroup) groups.push(currentGroup);
    return groups;
  }, [filteredItems]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
      {isExpanded && sections.length ? (
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-[0.04em] text-muted font-medium">{sectionTitle}</div>
          <div className="space-y-1">
            {sections.map((section) => (
              <button
                key={section.id}
                type="button"
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm text-text",
                  section.active && "bg-surface-muted"
                )}
                onClick={section.onClick}
                aria-label={section.label}
              >
                <span className="text-base">{section.icon}</span>
                <span className="whitespace-nowrap">{section.label}</span>
              </button>
            ))}
          </div>
          <div className="h-px bg-border" />
        </div>
      ) : null}
      {isExpanded ? (
        <div className="grid gap-2">
          {addActionLabel ? (
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.04em] text-muted font-medium">Liste</span>
              <Button label={addActionLabel} intent="ghost" size="sm" onClick={onAddAction} />
            </div>
          ) : null}
          <Input
            label="Recherche"
            placeholder={searchPlaceholder}
            hideLabel
            ariaLabel="Recherche"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Select label="Statut" options={filterOptions} hideLabel ariaLabel="Statut" />
        </div>
      ) : null}
      <div className="flex-1 min-h-0 space-y-2 overflow-y-auto overflow-x-hidden pr-1 pb-6 overscroll-contain" style={{ minHeight: 0 }}>
        {groupedItems.length > 0 ? (
          groupedItems.map((group) => {
            const isCollapsed = collapsedGroups.has(group.header.id);
            const isGroupHeader = group.header.groupHeader;

            return (
              <div key={group.header.id} className="space-y-1">
                {isGroupHeader ? (
                  <button
                    type="button"
                    className={cn(
                      "flex w-full items-center justify-between rounded-md border px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors",
                      "hover:opacity-90 cursor-pointer",
                      group.header.groupColor || "bg-gray-500/10 border-gray-500/30 text-gray-700 dark:text-gray-400"
                    )}
                    onClick={() => toggleGroup(group.header.id)}
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {isExpanded && (
                        <span className="text-xs flex-shrink-0">
                          {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                        </span>
                      )}
                      <span className="truncate">{group.header.title}</span>
                    </div>
                    <span className="text-xs font-normal whitespace-nowrap ml-2 flex-shrink-0">
                      {group.items.length} affiché{group.items.length !== 1 ? "s" : ""}
                    </span>
                  </button>
                ) : (
                  // Item without group header (shouldn't happen but handle it)
                  <button
                    key={group.header.id}
                    type="button"
                    className={cn(
                      "group flex w-full items-start gap-2 rounded-md border border-border bg-surface px-3 py-3 text-left text-sm text-text shadow-sm transition duration-150 ease-out",
                      "hover:-translate-y-0.5 hover:shadow-md hover:bg-surface-subtle",
                      selectedId === group.header.id && "border-primary/40 bg-surface-subtle border-l-[3px] border-l-primary",
                      !isExpanded && "justify-center"
                    )}
                    onClick={() => onSelect?.(group.header.id)}
                    aria-label={group.header.title}
                  >
                    <span className="relative">
                      <span className="text-base">{group.header.icon}</span>
                      {!isExpanded ? (
                        <span className="pointer-events-none absolute left-full top-1/2 z-10 ml-2 -translate-y-1/2 rounded-md border border-border bg-surface px-2 py-1 text-xs text-text opacity-0 transition group-hover:opacity-100 group-focus-visible:opacity-100">
                          {group.header.title}
                        </span>
                      ) : null}
                    </span>
                    {isExpanded ? (
                      <span className="flex min-w-0 flex-1 items-start justify-between gap-2">
                        <span className="min-w-0">
                          <span className="block truncate font-semibold text-text">{group.header.title}</span>
                          {group.header.subtitle ? <span className="block truncate text-xs text-muted">{group.header.subtitle}</span> : null}
                          {group.header.info ? <span className="block truncate text-xs text-muted">{group.header.info}</span> : null}
                        </span>
                        {group.header.status ? <Badge label={group.header.status.label} tone={group.header.status.tone} /> : null}
                      </span>
                    ) : null}
                  </button>
                )}
                {!isCollapsed && group.items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={cn(
                      "group flex w-full items-start gap-2 rounded-md border border-border bg-surface px-3 py-3 text-left text-sm text-text shadow-sm transition duration-150 ease-out",
                      "hover:-translate-y-0.5 hover:shadow-md hover:bg-surface-subtle",
                      selectedId === item.id && "border-primary/40 bg-surface-subtle border-l-[3px] border-l-primary",
                      !isExpanded && "justify-center"
                    )}
                    onClick={() => onSelect?.(item.id)}
                    aria-label={item.title}
                  >
                    <span className="relative">
                      <span className="text-base">{item.icon}</span>
                      {!isExpanded ? (
                        <span className="pointer-events-none absolute left-full top-1/2 z-10 ml-2 -translate-y-1/2 rounded-md border border-border bg-surface px-2 py-1 text-xs text-text opacity-0 transition group-hover:opacity-100 group-focus-visible:opacity-100">
                          {item.title}
                        </span>
                      ) : null}
                    </span>
                    {isExpanded ? (
                      <span className="flex min-w-0 flex-1 items-start justify-between gap-2">
                        <span className="min-w-0">
                          <span className="block truncate font-semibold text-text">{item.title}</span>
                          {item.subtitle ? <span className="block truncate text-xs text-muted">{item.subtitle}</span> : null}
                          {item.info ? <span className="block truncate text-xs text-muted">{item.info}</span> : null}
                        </span>
                        {item.status ? <Badge label={item.status.label} tone={item.status.tone} /> : null}
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            );
          })
        ) : (
          <div className="text-sm text-muted">{emptyMessage}</div>
        )}
      </div>
    </div>
  );
}
